import { mockKpiData } from "@/mocks/dashboard";

interface KpiItemProps {
  label: string;
  value: string;
  icon: string;
  color: string;
  bgColor: string;
  borderColor: string;
  sub?: string;
  trend?: "up" | "down" | "neutral";
}

function KpiItem({ label, value, icon, color, bgColor, borderColor, sub, trend }: KpiItemProps) {
  return (
    <div className={`flex items-center gap-3 p-3 rounded-xl border ${borderColor} ${bgColor} hover:border-opacity-60 transition-all group`}>
      <div className={`w-9 h-9 flex items-center justify-center rounded-lg bg-dark-card border ${borderColor} ${color} flex-shrink-0`}>
        <i className={`${icon} text-sm`} />
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-silver-dim text-xs truncate">{label}</p>
        <p className="text-white text-sm font-black" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>{value}</p>
        {sub && <p className="text-silver-dim text-xs">{sub}</p>}
      </div>
      {trend && (
        <i className={`text-sm flex-shrink-0 ${
          trend === "up" ? "ri-arrow-up-line text-cyber-green" :
          trend === "down" ? "ri-arrow-down-line text-cyber-red" :
          "ri-subtract-line text-silver-dim"
        }`} />
      )}
    </div>
  );
}

export default function KpiCards() {
  const kpi = mockKpiData;

  return (
    <div className="bg-dark-card border border-dark-border rounded-2xl overflow-hidden">
      <div className="flex items-center justify-between px-5 py-4 border-b border-dark-border">
        <div>
          <h2 className="text-white font-bold text-sm" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
            Key Performance Indicators
          </h2>
          <p className="text-silver-dim text-xs mt-0.5">Business health at a glance</p>
        </div>
        <span className="text-xs text-silver-dim border border-dark-border px-2.5 py-1 rounded-full">
          Last 90 days
        </span>
      </div>

      <div className="p-4 grid grid-cols-1 sm:grid-cols-2 gap-2">
        <KpiItem
          label="On-Time Delivery"
          value={`${kpi.onTimeDeliveryRate}%`}
          icon="ri-time-line"
          color="text-cyber-green"
          bgColor="bg-cyber-green/5"
          borderColor="border-cyber-green/20"
          sub="Projects delivered on schedule"
          trend="up"
        />
        <KpiItem
          label="Client Satisfaction"
          value={`${kpi.clientSatisfaction}/5.0`}
          icon="ri-star-line"
          color="text-cyber-orange"
          bgColor="bg-cyber-orange/5"
          borderColor="border-cyber-orange/20"
          sub="Average rating across all clients"
          trend="up"
        />
        <KpiItem
          label="Ticket Resolution Rate"
          value={`${kpi.ticketResolutionRate}%`}
          icon="ri-customer-service-line"
          color="text-electric-blue"
          bgColor="bg-electric-blue/5"
          borderColor="border-electric-blue/20"
          sub="Tickets closed within SLA"
          trend="up"
        />
        <KpiItem
          label="Avg Resolution Time"
          value={`${kpi.avgTicketResolutionHrs}h`}
          icon="ri-timer-line"
          color="text-cyber-green"
          bgColor="bg-cyber-green/5"
          borderColor="border-cyber-green/20"
          sub="Mean time to resolve tickets"
          trend="up"
        />
        <KpiItem
          label="Revenue per Client"
          value={`$${kpi.revenuePerClient.toLocaleString()}`}
          icon="ri-money-dollar-circle-line"
          color="text-cyber-orange"
          bgColor="bg-cyber-orange/5"
          borderColor="border-cyber-orange/20"
          sub="Average LTV this quarter"
          trend="up"
        />
        <KpiItem
          label="Team Utilization"
          value={`${kpi.utilizationRate}%`}
          icon="ri-team-line"
          color="text-electric-blue"
          bgColor="bg-electric-blue/5"
          borderColor="border-electric-blue/20"
          sub="Billable hours vs capacity"
          trend="neutral"
        />
        <KpiItem
          label="Repeat Client Rate"
          value={`${kpi.repeatClientRate}%`}
          icon="ri-refresh-line"
          color="text-cyber-green"
          bgColor="bg-cyber-green/5"
          borderColor="border-cyber-green/20"
          sub="Clients with 2+ projects"
          trend="up"
        />
        <KpiItem
          label="Avg Project Duration"
          value={`${kpi.avgProjectDuration} mo`}
          icon="ri-calendar-line"
          color="text-silver"
          bgColor="bg-dark-panel/50"
          borderColor="border-dark-border"
          sub="From kickoff to launch"
          trend="neutral"
        />
      </div>
    </div>
  );
}
